<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-hidden">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black bg-opacity-75" @click="close" />
      
      <!-- Scanner Modal -->
      <div class="relative max-w-lg mx-auto mt-10 bg-white rounded-t-2xl">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-semibold">Kennzeichen scannen</h3>
          <button @click="close" class="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Camera View -->
        <div class="relative aspect-[4/3] bg-black">
          <video 
            ref="videoRef" 
            class="w-full h-full object-cover"
            autoplay 
            playsinline
          ></video>
          
          <!-- Targeting Overlay -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="scanner-frame">
              <div class="corner top-left"></div>
              <div class="corner top-right"></div>
              <div class="corner bottom-left"></div>
              <div class="corner bottom-right"></div>
            </div>
          </div>
          
          <!-- Status Messages -->
          <div class="absolute top-4 left-4 right-4 space-y-2">
            <!-- Ready State Message -->
            <div v-if="status === 'ready'" class="bg-green-500/80 text-white px-3 py-2 rounded-lg text-sm text-center">
              <div class="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span v-if="attemptCount === 0">Positionieren Sie das Kennzeichen im Rahmen</span>
                <span v-else>Versuchen Sie es erneut</span>
              </div>
            </div>
            
            <!-- Scanning State Message -->
            <div v-if="status === 'scanning'" class="bg-blue-500/80 text-white px-3 py-2 rounded-lg text-sm text-center">
              <div class="flex items-center justify-center">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="attemptCount <= 1">Erkenne Kennzeichen...</span>
                <span v-else>Analysiere Bild erneut...</span>
              </div>
            </div>
            
            <!-- Error State Message -->
            <div v-if="status === 'error'" class="space-y-2">
              <!-- Main Error Message -->
              <div class="bg-red-500/90 text-white px-4 py-3 rounded-lg text-sm shadow-lg">
                <div class="flex items-start justify-center">
                  <!-- Dynamic icon based on error type -->
                  <svg v-if="lastErrorType === 'scan_failed'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  </svg>
                  <svg v-else-if="lastErrorType === 'network_error'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="text-left">{{ errorMessage }}</span>
                </div>
              </div>
              
              <!-- Additional Tips/Suggestions -->
              <div v-if="showSuggestions && lastErrorType === 'scan_failed'" class="bg-yellow-500/80 text-yellow-900 px-3 py-2 rounded-lg text-xs">
                <div class="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <div class="text-left">
                    <div class="font-medium mb-1">Tipps für bessere Ergebnisse:</div>
                    <ul class="space-y-1 text-xs">
                      <li>• Sorgen Sie für gute Beleuchtung</li>
                      <li>• Halten Sie die Kamera ruhig</li>
                      <li>• Kennzeichen sollte komplett sichtbar sein</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <!-- Manual Entry Suggestion -->
              <div v-if="attemptCount >= 3" class="bg-blue-500/80 text-white px-3 py-2 rounded-lg text-xs text-center">
                <div class="flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Tipp: Nutzen Sie die manuelle Eingabe unten
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Actions -->
        <div class="p-4 bg-gray-50">
          <button 
            @click="capture"
            :disabled="status === 'scanning' || status === 'initializing'"
            class="w-full py-3 bg-[#E9041D] text-white rounded-lg font-medium transition-colors"
            :class="{ 
              'opacity-50 cursor-not-allowed': status === 'scanning' || status === 'initializing',
              'hover:bg-red-700': status === 'ready'
            }"
          >
            <svg v-if="status !== 'scanning'" xmlns="http://www.w3.org/2000/svg" class="inline h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <svg v-else class="inline animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ status === 'scanning' ? 'Verarbeite...' : status === 'initializing' ? 'Kamera startet...' : 'Scannen' }}
          </button>
          
          <button 
            @click="enterManually"
            class="w-full mt-2 py-2 text-gray-600 text-sm hover:text-gray-800 transition-colors"
          >
            Kennzeichen manuell eingeben
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import api from '../api/axios'

const props = defineProps<{
  isOpen: boolean
  userRole?: string
}>()

const emit = defineEmits<{
  close: []
  scanned: [licensePlate: string]
  manual: []
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const stream = ref<MediaStream | null>(null)
const status = ref<'initializing' | 'ready' | 'scanning' | 'error'>('initializing')
const errorMessage = ref('')
const attemptCount = ref(0)
const lastErrorType = ref('')
const showSuggestions = ref(false)

// Error code to configuration mapping (backend provides German messages now)
const errorCodeConfigurations: Record<string, {
  progressive: string[]
  icon: string
  suggestions: string[]
  showTips: boolean
  forceManual?: boolean
}> = {
  'NO_PLATE_DETECTED': {
    progressive: [
      'Kein Kennzeichen erkannt. Positionieren Sie es mittig im Rahmen.',
      'Immer noch kein Kennzeichen erkannt. Sorgen Sie für gute Beleuchtung.',
      'Kennzeichen schwer erkennbar. Verwenden Sie die manuelle Eingabe.'
    ],
    icon: 'camera',
    suggestions: ['Kennzeichen mittig positionieren', 'Bessere Beleuchtung', 'Näher herangehen'],
    showTips: true
  },
  'IMAGE_TOO_BLURRY': {
    progressive: [
      'Bild ist unscharf. Halten Sie die Kamera ruhig.',
      'Bild immer noch unscharf. Sorgen Sie für bessere Beleuchtung.',
      'Bildqualität problematisch. Nutzen Sie die manuelle Eingabe.'
    ],
    icon: 'camera',
    suggestions: ['Kamera ruhig halten', 'Auf Fokus warten', 'Bessere Beleuchtung'],
    showTips: true
  },
  'MULTIPLE_PLATES': {
    progressive: [
      'Mehrere Kennzeichen erkannt. Fokussieren Sie auf eines.',
      'Immer noch mehrere Kennzeichen. Gehen Sie näher heran.',
      'Zu viele Kennzeichen im Bild. Manuelle Eingabe empfohlen.'
    ],
    icon: 'warning',
    suggestions: ['Nur ein Kennzeichen im Bild', 'Näher herangehen', 'Anderen Winkel wählen'],
    showTips: true
  },
  'PLATE_PARTIALLY_OBSCURED': {
    progressive: [
      'Kennzeichen teilweise verdeckt. Ändern Sie den Blickwinkel.',
      'Kennzeichen nicht vollständig sichtbar. Andere Position versuchen.',
      'Kennzeichen verdeckt. Manuelle Eingabe verwenden.'
    ],
    icon: 'warning',
    suggestions: ['Blickwinkel ändern', 'Andere Position', 'Hindernisse entfernen'],
    showTips: true
  },
  'POOR_LIGHTING': {
    progressive: [
      'Schlechte Lichtverhältnisse. Bessere Beleuchtung suchen.',
      'Beleuchtung immer noch unzureichend. Andere Lichtquelle nutzen.',
      'Licht zu schwach. Manuelle Eingabe empfohlen.'
    ],
    icon: 'light',
    suggestions: ['Bessere Beleuchtung', 'Tageslicht nutzen', 'Taschenlampe verwenden'],
    showTips: true
  },
  'IMAGE_TOO_SMALL': {
    progressive: [
      'Kennzeichen zu klein. Gehen Sie näher heran.',
      'Immer noch zu weit entfernt. Näher zum Kennzeichen.',
      'Zu große Distanz. Manuelle Eingabe verwenden.'
    ],
    icon: 'camera',
    suggestions: ['Näher herangehen', 'Zoom verwenden falls verfügbar', 'Kennzeichen größer im Bild'],
    showTips: true
  },
  'AI_SERVICE_UNAVAILABLE': {
    progressive: [
      'KI-Service nicht verfügbar. Bitte verwenden Sie die manuelle Eingabe.',
      'Scanner-Service offline. Manuelle Eingabe erforderlich.',
      'Scanning-Funktion vorübergehend nicht verfügbar.'
    ],
    icon: 'offline',
    suggestions: ['Manuelle Eingabe verwenden', 'Später erneut versuchen'],
    showTips: false,
    forceManual: true
  },
  'RATE_LIMIT_EXCEEDED': {
    progressive: [
      'Zu viele Scan-Versuche. Bitte warten Sie einen Moment.',
      'Rate Limit erreicht. Kurz pausieren und dann erneut versuchen.',
      'Zu häufige Anfragen. Manuelle Eingabe verwenden.'
    ],
    icon: 'warning',
    suggestions: ['Einen Moment warten', 'Manuelle Eingabe verwenden'],
    showTips: false
  },
  'INVALID_FORMAT': {
    progressive: [
      'Kein gültiges Kennzeichen erkannt. Versuchen Sie es erneut.',
      'Format nicht erkannt. Bessere Bildqualität erforderlich.',
      'Ungültiges Format. Manuelle Eingabe verwenden.'
    ],
    icon: 'warning',
    suggestions: ['Kennzeichen komplett sichtbar', 'Bessere Bildqualität', 'Andere Position'],
    showTips: true
  },
  'AI_SERVICE_ERROR': {
    progressive: [
      'Fehler beim KI-Service. Bitte erneut versuchen.',
      'KI-Service Problem. Kurz warten und erneut versuchen.',
      'Service-Fehler. Manuelle Eingabe empfohlen.'
    ],
    icon: 'warning',
    suggestions: ['Kurz warten', 'Erneut versuchen', 'Manuelle Eingabe'],
    showTips: false
  },
  'SCAN_PROCESSING_ERROR': {
    progressive: [
      'Fehler bei der Bildverarbeitung. Erneut versuchen.',
      'Verarbeitungsfehler. Anderes Bild verwenden.',
      'Bildverarbeitung fehlgeschlagen. Manuelle Eingabe nutzen.'
    ],
    icon: 'warning',
    suggestions: ['Anderes Bild versuchen', 'Bessere Bildqualität', 'Manuelle Eingabe'],
    showTips: true
  },
  'UNKNOWN_ERROR': {
    progressive: [
      'Unbekannter Fehler. Bitte erneut versuchen.',
      'Fehler beim Scannen. Andere Aufnahme versuchen.',
      'Scanning fehlgeschlagen. Manuelle Eingabe verwenden.'
    ],
    icon: 'warning',
    suggestions: ['Erneut versuchen', 'Bessere Bedingungen', 'Manuelle Eingabe'],
    showTips: true
  }
}

// Get progressive message based on error code and attempt count
const getProgressiveMessageByCode = (errorCode: string, attempt: number): string => {
  const config = errorCodeConfigurations[errorCode]
  if (!config) {
    return 'Unbekannter Fehler beim Scannen. Bitte erneut versuchen.'
  }
  
  const messageIndex = Math.min(attempt - 1, config.progressive.length - 1)
  return config.progressive[messageIndex]
}

// Enhanced error handling with error codes from backend
const handleErrorWithGuidance = (error: any): string => {
  let errorCode = 'UNKNOWN_ERROR'
  let baseMessage = ''
  
  // Check for structured error response from backend
  if (error.response?.data?.error_code) {
    errorCode = error.response.data.error_code
    baseMessage = error.response.data.message || 'Unbekannter Fehler'
    lastErrorType.value = errorCode
    
    // Use progressive message from configuration
    return getProgressiveMessageByCode(errorCode, attemptCount.value)
  }
  
  // Fallback for different response types
  if (error.response?.status === 422) {
    // Legacy response format - try to extract message
    baseMessage = error.response?.data?.message || 'Scanning fehlgeschlagen'
    errorCode = 'NO_PLATE_DETECTED' // Most likely case for 422
    lastErrorType.value = errorCode
    return getProgressiveMessageByCode(errorCode, attemptCount.value)
  } else if (error.response?.status === 429) {
    errorCode = 'RATE_LIMIT_EXCEEDED'
    lastErrorType.value = errorCode
    return getProgressiveMessageByCode(errorCode, attemptCount.value)
  } else if (error.response?.status === 503) {
    errorCode = 'AI_SERVICE_UNAVAILABLE'
    lastErrorType.value = errorCode
    return getProgressiveMessageByCode(errorCode, attemptCount.value)
  } else if (error.response?.status === 500) {
    errorCode = 'AI_SERVICE_ERROR'
    lastErrorType.value = errorCode
    baseMessage = 'Serverfehler. Bitte versuchen Sie es später erneut.'
  } else if (error.message) {
    if (error.message.includes('Network')) {
      errorCode = 'NETWORK_ERROR'
      lastErrorType.value = errorCode
      baseMessage = 'Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung.'
    } else {
      lastErrorType.value = 'unknown_error'
      baseMessage = error.message
    }
  } else {
    lastErrorType.value = 'unknown_error'
    baseMessage = 'Fehler beim Scannen. Bitte erneut versuchen.'
  }
  
  return baseMessage
}

// Watch for modal open/close
watch(() => props.isOpen, async (newValue) => {
  if (newValue) {
    await nextTick()
    await initializeCamera()
  } else {
    stopCamera()
  }
})

const initializeCamera = async () => {
  try {
    status.value = 'initializing'
    errorMessage.value = ''
    
    // Request camera access
    stream.value = await navigator.mediaDevices.getUserMedia({
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    })
    
    // Connect stream to video element
    if (videoRef.value) {
      videoRef.value.srcObject = stream.value
      
      // Wait for video to load
      await new Promise<void>((resolve) => {
        if (videoRef.value) {
          videoRef.value.onloadedmetadata = () => {
            resolve()
          }
        }
      })
      
      status.value = 'ready'
    }
  } catch (error) {
    status.value = 'error'
    errorMessage.value = 'Kamera konnte nicht gestartet werden'
    console.error('Camera initialization failed:', error)
  }
}

const capture = async () => {
  if (!videoRef.value || status.value === 'scanning') return
  
  status.value = 'scanning'
  
  try {
    // Create canvas and capture frame
    const canvas = document.createElement('canvas')
    canvas.width = videoRef.value.videoWidth
    canvas.height = videoRef.value.videoHeight
    const ctx = canvas.getContext('2d')
    
    if (!ctx) throw new Error('Canvas context not available')
    
    // Draw current frame
    ctx.drawImage(videoRef.value, 0, 0, canvas.width, canvas.height)
    
    // Convert to base64 with optimal quality
    const imageData = canvas.toDataURL('image/jpeg', 0.85)
    
    // Send to backend for OCR processing
    // Use the user role as context, default to 'driver' for key handover
    const response = await api.post('/api/scan-plate', {
      image: imageData,
      context: props.userRole || 'driver'
    })
    
    if (response.data.success && response.data.license_plate) {
      // Success! Reset attempt counter and emit the scanned plate
      attemptCount.value = 0
      lastErrorType.value = ''
      emit('scanned', response.data.license_plate)
      close()
    } else {
      // Increment attempt counter
      attemptCount.value++
      
      // Handle structured error response from backend
      if (response.data.error_code) {
        // Backend provided structured error response with German message
        const errorCode = response.data.error_code
        lastErrorType.value = errorCode
        
        // Use progressive message from configuration based on error code and attempt count
        const progressiveMessage = getProgressiveMessageByCode(errorCode, attemptCount.value)
        
        // Set error state directly without throwing
        status.value = 'error'
        errorMessage.value = progressiveMessage
        
        // Show suggestions after multiple failed attempts
        showSuggestions.value = attemptCount.value >= 2
        
        // Progressive reset timing based on attempt count
        const resetDelay = attemptCount.value === 1 ? 3000 : attemptCount.value === 2 ? 4000 : 5000
        setTimeout(() => {
          if (status.value === 'error') {
            status.value = 'ready'
            errorMessage.value = ''
            showSuggestions.value = false
          }
        }, resetDelay)
        
        return // Exit early, don't throw error
      } else {
        // Fallback for legacy responses
        const errorMsg = response.data.message || 'Kennzeichen konnte nicht erkannt werden'
        throw new Error(errorMsg)
      }
    }
  } catch (error: any) {
    status.value = 'error'
    
    // Enhanced error handling with progressive guidance
    const translatedMessage = handleErrorWithGuidance(error)
    errorMessage.value = translatedMessage
    
    console.error('Scanner error details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      attempt: attemptCount.value
    })
    
    // Show suggestions after multiple failed attempts
    showSuggestions.value = attemptCount.value >= 2
    
    // Progressive reset timing based on attempt count
    const resetDelay = attemptCount.value === 1 ? 3000 : attemptCount.value === 2 ? 4000 : 5000
    setTimeout(() => {
      if (status.value === 'error') {
        status.value = 'ready'
        errorMessage.value = ''
        showSuggestions.value = false
      }
    }, resetDelay)
  }
}

const stopCamera = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
    stream.value = null
  }
}

const close = () => {
  // Reset state when closing
  attemptCount.value = 0
  lastErrorType.value = ''
  showSuggestions.value = false
  stopCamera()
  emit('close')
}

const enterManually = () => {
  close()
  emit('manual')
}
</script>

<style scoped>
.scanner-frame {
  width: 280px;
  height: 80px;
  position: relative;
  border: 2px solid #E9041D;
  border-radius: 8px;
  animation: pulse 2s infinite;
}

.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid white;
}

.corner.top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
  border-radius: 8px 0 0 0;
}

.corner.top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
  border-radius: 0 8px 0 0;
}

.corner.bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
  border-radius: 0 0 0 8px;
}

.corner.bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
  border-radius: 0 0 8px 0;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
</style>